// src/components/ClientPortalLogin.js

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-toastify';
import { FileSignature, Receipt, Briefcase, ShieldCheck, ArrowRight, Mail, Lock } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const Spin = () => (
  <svg
    className="animate-spin -ml-1 mr-3 h-5 w-5"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    ></path>
  </svg>
);

const inputClass =
  'appearance-none block w-full px-3.5 py-2.5 rounded-md text-sm border border-grayteck-300 dark:border-darkbg-500 bg-white dark:bg-darkbg-700 text-grayteck-900 dark:text-grayteck-100 placeholder:text-grayteck-400 dark:placeholder:text-grayteck-500 focus:outline-none focus:ring-2 focus:ring-grayteck-600 dark:focus:ring-gold-400 focus:border-transparent transition-all duration-200';

const labelClass =
  'block text-xs font-bold uppercase tracking-caps text-grayteck-700 dark:text-grayteck-400 mb-1.5';

const primaryBtnClass =
  'group relative w-full flex justify-center items-center py-2.5 px-4 text-sm font-bold rounded-md text-accentfg bg-gold-400 hover:bg-gold-300 active:bg-gold-500 shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

const secondaryBtnClass =
  'w-full flex justify-center items-center py-2.5 px-4 text-sm font-semibold rounded-md text-grayteck-700 dark:text-grayteck-200 border border-grayteck-300 dark:border-darkbg-500 bg-white dark:bg-darkbg-700 hover:bg-grayteck-100 dark:hover:bg-darkbg-600 hover:border-grayteck-400 dark:hover:border-grayteck-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

const FEATURES = [
  { icon: FileSignature, text: 'Review and sign documents securely' },
  { icon: Receipt, text: 'Track and pay invoices in one place' },
  { icon: Briefcase, text: 'Monitor your services and subscriptions' },
];

const ClientPortalLogin = () => {
  const nav = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors }, getValues, trigger } = useForm();
  const {
    sendAccessCode,
    verifyAccessCode,
    isSendingCode,
    isVerifyingCode,
    loginWithPassword,
    isPasswordLoggingIn,
  } = useAuth();

  const [email, setEmail] = useState('');
  // Whether we mailed the code on this visit, or the client already had one (from
  // an earlier email, or read to them by an admin). Only changes the step-2 copy.
  const [codeWasJustSent, setCodeWasJustSent] = useState(false);
  // Which sign-in method is active: emailed one-time code (existing flow) or
  // email + password. Both mint the same 24h portal session.
  const [method, setMethod] = useState('code');

  // Separate form instance for the password panel so its validation state
  // never crosses into the access-code form.
  const {
    register: registerPw,
    handleSubmit: handleSubmitPw,
    formState: { errors: pwErrors },
  } = useForm();

  // ClientRoute bounces an unauthenticated deep link here and hands us the page the
  // client was actually trying to reach (state.from). Without this we always dropped
  // them on the dashboard, so the "View in Portal" button in an invoice email lost
  // the invoice it was pointing at. Only same-site paths are honored.
  const requestedPath = location.state?.from;
  const destination =
    typeof requestedPath === 'string' && requestedPath.startsWith('/') && !requestedPath.startsWith('//')
      ? requestedPath
      : '/client-portal-dash';

  const switchMethod = (next) => {
    setMethod(next);
    // Reset the access-code step machine so switching back starts clean.
    setEmail('');
    setCodeWasJustSent(false);
  };

  // Handler to send access code
  const onSubmitSendCode = async (data) => {
    try {
      await sendAccessCode.mutateAsync({ email: data.email });
      setEmail(data.email);
      setCodeWasJustSent(true);
    } catch (error) {
      console.error('Error sending access code:', error);
    }
  };

  // Requesting a code mints a new one and invalidates any outstanding code, so the
  // send step was also the only way to reach the code box. That left no way to use
  // a code you already have: one an admin read to you over the phone, or one from
  // an earlier email you came back to. This advances to the code box without
  // touching the outstanding code.
  const onUseExistingCode = async () => {
    const emailIsValid = await trigger('email');
    if (!emailIsValid) return;
    setEmail(getValues('email'));
    setCodeWasJustSent(false);
  };

  // Handler to verify access code
  const onSubmitVerifyCode = async (data) => {
    try {
      await verifyAccessCode.mutateAsync({ email, code: data.code });
      nav(destination, { replace: true });
    } catch (error) {
      console.error('Error verifying access code:', error);
    }
  };

  // Handler for email + password sign-in
  const onSubmitPasswordLogin = async (data) => {
    try {
      await loginWithPassword.mutateAsync({
        email: data.email,
        password: data.password,
      });
      nav(destination, { replace: true });
    } catch (error) {
      console.error('Error signing in with password:', error);
    }
  };

  return (
    <div className="min-h-screen flex bg-grayteck-50 dark:bg-darkbg-900">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[44%] xl:w-2/5 relative flex-col justify-between bg-grayteck-900 dark:bg-darkbg-800 border-r border-grayteck-800 dark:border-darkbg-600 p-12">
        <span className="absolute left-0 top-0 h-full w-1 bg-gold-400" />

        <div>
          <span className="font-display text-2xl font-bold tracking-tight text-white">
            GrayTeck<span className="text-gold-400">.</span>
          </span>
          <span className="block mt-1 text-[11px] font-semibold uppercase tracking-caps text-grayteck-400">
            Client Portal
          </span>
        </div>

        <div>
          <h1 className="font-display text-3xl xl:text-4xl font-bold text-white leading-tight tracking-tight max-w-md">
            Your engagement with us, all in one place.
          </h1>
          <ul className="mt-10 space-y-5">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3.5">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 border border-white/10">
                  <Icon className="w-4 h-4 text-gold-400" />
                </span>
                <span className="text-[15px] text-grayteck-300">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-2 text-grayteck-400">
          <ShieldCheck className="w-4 h-4 text-gold-400" />
          <span className="text-xs">
            Sign in with a one-time emailed access code, or with your password.
          </span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full">
          {/* Compact brand for small screens */}
          <div className="lg:hidden text-center mb-10">
            <span className="font-display text-2xl font-bold tracking-tight text-grayteck-900 dark:text-grayteck-100">
              GrayTeck<span className="text-gold-400">.</span>
            </span>
            <span className="block mt-1 text-[11px] font-semibold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400">
              Client Portal
            </span>
          </div>

          <h2 className="font-display text-2xl font-bold text-grayteck-900 dark:text-grayteck-100 tracking-tight">
            Sign in
          </h2>
          <p className="mt-1.5 mb-6 text-sm text-grayteck-700 dark:text-grayteck-400">
            {method === 'password'
              ? 'Sign in with your email and password.'
              : !email
                ? 'Enter your email and we will send you a one-time access code.'
                : 'Check your inbox for the access code.'}
          </p>

          {/* Method toggle: emailed code vs password */}
          <div className="mb-6 grid grid-cols-2 gap-1 p-1 rounded-lg bg-grayteck-100 dark:bg-darkbg-700 border border-grayteck-200 dark:border-darkbg-600">
            <button
              type="button"
              onClick={() => switchMethod('code')}
              aria-pressed={method === 'code'}
              className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-bold uppercase tracking-caps transition-colors ${
                method === 'code'
                  ? 'bg-white dark:bg-darkbg-800 text-grayteck-900 dark:text-grayteck-100 shadow-sm'
                  : 'text-grayteck-600 dark:text-grayteck-400 hover:text-grayteck-800 dark:hover:text-grayteck-200'
              }`}
            >
              <Mail className="w-3.5 h-3.5" />
              Email a code
            </button>
            <button
              type="button"
              onClick={() => switchMethod('password')}
              aria-pressed={method === 'password'}
              className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-bold uppercase tracking-caps transition-colors ${
                method === 'password'
                  ? 'bg-white dark:bg-darkbg-800 text-grayteck-900 dark:text-grayteck-100 shadow-sm'
                  : 'text-grayteck-600 dark:text-grayteck-400 hover:text-grayteck-800 dark:hover:text-grayteck-200'
              }`}
            >
              <Lock className="w-3.5 h-3.5" />
              Password
            </button>
          </div>

          {method === 'password' ? (
            /* Email + password sign-in */
            <form className="space-y-5" onSubmit={handleSubmitPw(onSubmitPasswordLogin)}>
              <div>
                <label htmlFor="pw-email" className={labelClass}>Email</label>
                <div className="relative">
                  <input
                    id="pw-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    {...registerPw('email', { required: 'Email is required' })}
                    className={`${inputClass} pl-10`}
                    placeholder="you@company.com"
                    disabled={isPasswordLoggingIn}
                  />
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grayteck-400" />
                </div>
                {pwErrors.email && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{pwErrors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="pw-password" className={`${labelClass} mb-0`}>Password</label>
                  <Link
                    to="/client-portal-reset"
                    className="text-xs font-semibold text-grayteck-600 dark:text-grayteck-400 hover:text-grayteck-900 dark:hover:text-gold-400 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="pw-password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    {...registerPw('password', { required: 'Password is required' })}
                    className={`${inputClass} pl-10`}
                    placeholder="••••••••"
                    disabled={isPasswordLoggingIn}
                  />
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grayteck-400" />
                </div>
                {pwErrors.password && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{pwErrors.password.message}</p>
                )}
              </div>

              {/* Error Message */}
              {loginWithPassword.error && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-400/20 p-3.5">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                    {loginWithPassword.error.detail || 'Invalid email or password'}
                  </h3>
                </div>
              )}

              <button
                type="submit"
                disabled={isPasswordLoggingIn}
                className={primaryBtnClass}
              >
                {isPasswordLoggingIn ? (
                  <>
                    <Spin />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>

              <p className="text-center text-xs text-grayteck-500 dark:text-grayteck-500">
                No password yet? Sign in with an emailed code, then set one in Settings.
              </p>
            </form>
          ) : !email ? (
            <form className="space-y-5" onSubmit={handleSubmit(onSubmitSendCode)}>
              <div>
                <label htmlFor="email" className={labelClass}>Email</label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    {...register('email', { required: 'Email is required' })}
                    className={`${inputClass} pl-10`}
                    placeholder="you@company.com"
                    disabled={isSendingCode}
                  />
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grayteck-400" />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
                )}
              </div>

              {/* Error Message */}
              {sendAccessCode.error && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-400/20 p-3.5">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                    {sendAccessCode.error.detail || 'An error occurred while sending access code'}
                  </h3>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSendingCode}
                className={primaryBtnClass}
              >
                {isSendingCode ? (
                  <>
                    <Spin />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Access Code
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>

              <p className="text-center text-xs text-grayteck-500 dark:text-grayteck-500">
                Already have a code?{' '}
                <button
                  type="button"
                  onClick={onUseExistingCode}
                  disabled={isSendingCode}
                  className="font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-60"
                >
                  Enter it here
                </button>
              </p>
            </form>
          ) : (
            /* Step 2: Access Code Verification with Resend Option */
            <div className="space-y-5">
              <div className="rounded-md bg-grayteck-100 dark:bg-darkbg-700 border border-grayteck-200 dark:border-darkbg-600 px-3.5 py-2.5">
                <p className="text-sm text-grayteck-700 dark:text-grayteck-300">
                  {codeWasJustSent ? 'Access code sent to' : 'Signing in as'}{' '}
                  <span className="font-semibold text-grayteck-900 dark:text-grayteck-100">{email}</span>
                </p>
                {!codeWasJustSent && (
                  <p className="mt-1 text-xs text-grayteck-500 dark:text-grayteck-400">
                    Enter the code you already have. Resending will replace it with a
                    new one.
                  </p>
                )}
              </div>
              <form onSubmit={handleSubmit(onSubmitVerifyCode)}>
                <div>
                  <label htmlFor="code" className={labelClass}>Access Code</label>
                  <input
                    id="code"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    {...register('code', { required: 'Access code is required' })}
                    className={`${inputClass} tracking-[0.35em] font-semibold text-center`}
                    placeholder="••••••"
                    disabled={isVerifyingCode}
                  />
                  {errors.code && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.code.message}</p>
                  )}
                </div>

                {/* Error Message */}
                {verifyAccessCode.error && (
                  <div className="rounded-md bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-400/20 p-3.5 mt-4">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                      {verifyAccessCode.error.detail || 'Invalid access code'}
                    </h3>
                  </div>
                )}

                {/* Buttons Container */}
                <div className="mt-6 space-y-3">
                  {/* Verify Button */}
                  <button
                    type="submit"
                    disabled={isVerifyingCode}
                    className={primaryBtnClass}
                  >
                    {isVerifyingCode ? (
                      <>
                        <Spin />
                        Verifying...
                      </>
                    ) : (
                      'Verify Access Code'
                    )}
                  </button>

                  {/* Resend Button */}
                  <button
                    type="button"
                    onClick={() => onSubmitSendCode({ email })}
                    disabled={isSendingCode}
                    className={secondaryBtnClass}
                  >
                    {isSendingCode ? (
                      <>
                        <Spin />
                        Resending...
                      </>
                    ) : (
                      'Resend Access Code'
                    )}
                  </button>

                  {/* Typo'd email? Start over instead of waiting on a code
                      that will never arrive. */}
                  <button
                    type="button"
                    onClick={() => setEmail('')}
                    disabled={isVerifyingCode || isSendingCode}
                    className="w-full text-center text-xs font-semibold text-grayteck-600 dark:text-grayteck-400 hover:text-grayteck-900 dark:hover:text-gold-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Use a different email
                  </button>
                </div>
              </form>
            </div>
          )}

          <p className="mt-10 text-center text-xs text-grayteck-500 dark:text-grayteck-500">
            © {new Date().getFullYear()} GrayTecknologies LLC
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientPortalLogin;
